import { useState, useEffect, useCallback, useRef } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  Alert,
  ButtonIcon,
  Flex,
  Grid,
  Skeleton,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';
import { RiRefreshLine } from '@remixicon/react';
import { useServiceBusConfig } from './hooks/useServiceBusConfig';
import { TopicList } from './components/TopicList';
import { SubscriptionList } from './components/SubscriptionList';
import {
  ServiceBusFormData,
  ServiceBusTopicManagerOptions,
  ServiceBusSubscription,
  ServiceBusTopic,
  TrackedTopic,
  TrackedSubscription,
} from './types';
import { computeChangeset } from './computeChangeset';
import styles from './ServiceBusTopicManager.module.css';

/**
 * ServiceBusTopicManager — Custom Scaffolder Field Extension
 *
 * Provides an interactive UI for managing Azure Service Bus topics
 * and subscriptions by reading from and preparing updates to Terraform
 * configuration files.
 */
export const ServiceBusTopicManager = ({
  onChange,
  rawErrors,
  schema,
  uiSchema,
  formContext,
}: FieldExtensionComponentProps<
  ServiceBusFormData,
  ServiceBusTopicManagerOptions
>) => {
  const config: ServiceBusTopicManagerOptions = uiSchema?.['ui:options'] || {
    projectId: 0,
    repoPath: '',
  };

  // Get the environment from the form context (sibling field)
  const formData = (formContext as any)?.formData;
  const environment: string = formData?.environment || '';

  const {
    topics: remoteTopics,
    subscriptions: remoteSubs,
    loading,
    error,
    reload,
  } = useServiceBusConfig(config.projectId, environment);

  // Tracked state with change tracking
  const [trackedTopics, setTrackedTopics] = useState<TrackedTopic[]>([]);
  const [trackedSubs, setTrackedSubs] = useState<TrackedSubscription[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  const originalSnapshotRef = useRef<{
    topics: ServiceBusTopic[];
    subscriptions: ServiceBusSubscription[];
  }>({ topics: [], subscriptions: [] });
  const lastEmittedRef = useRef<string>();

  // Initialize from remote data
  useEffect(() => {
    if (!loading && remoteTopics.length >= 0 && !initialized) {
      setTrackedTopics(
        remoteTopics.map(t => ({
          ...t,
          id: `topic:${t.name}`,
          status: 'original' as const,
        })),
      );
      setTrackedSubs(
        remoteSubs.map(s => ({
          ...s,
          id: `sub:${s.topic}/${s.name}`,
          status: 'original' as const,
        })),
      );
      originalSnapshotRef.current = {
        topics: remoteTopics.map(({ name }) => ({ name })),
        subscriptions: remoteSubs.map(s => ({ ...s })),
      };
      setInitialized(true);
    }
  }, [remoteTopics, remoteSubs, loading, initialized]);

  // Reset when environment changes
  useEffect(() => {
    setInitialized(false);
    setSelectedTopic(null);
    setTrackedTopics([]);
    setTrackedSubs([]);
    originalSnapshotRef.current = { topics: [], subscriptions: [] };
    lastEmittedRef.current = undefined;
  }, [environment]);

  // Emit changeset (diff vs original snapshot) for review and backend
  useEffect(() => {
    if (!initialized) return;

    const changes = computeChangeset(
      trackedTopics,
      trackedSubs,
      originalSnapshotRef.current.topics,
      originalSnapshotRef.current.subscriptions,
    );
    const serialized = JSON.stringify({ changes });
    if (lastEmittedRef.current === serialized) {
      return;
    }
    lastEmittedRef.current = serialized;
    onChange({ changes });
  }, [trackedTopics, trackedSubs, initialized, onChange]);

  // Topic CRUD handlers
  const handleAddTopic = useCallback((name: string) => {
    setTrackedTopics(prev => [
      ...prev,
      { name, id: `topic:${name}`, status: 'added' },
    ]);
  }, []);

  const handleEditTopic = useCallback(
    (id: string, newName: string) => {
      setTrackedTopics(prev => {
        const target = prev.find(t => t.id === id);
        if (!target || target.name === newName) return prev;
        const oldName = target.name;

        // Update subscriptions referencing this topic
        setTrackedSubs(prevSubs =>
          prevSubs.map(s => {
            if (s.topic !== oldName) return s;
            return {
              ...s,
              topic: newName,
              status: s.status === 'added' ? 'added' : 'modified',
            };
          }),
        );

        if (selectedTopic === oldName) {
          setSelectedTopic(newName);
        }

        return prev.map(t => {
          if (t.id !== id) return t;
          return {
            ...t,
            name: newName,
            status: t.status === 'added' ? 'added' : 'modified',
          };
        });
      });
    },
    [selectedTopic],
  );

  const handleDeleteTopic = useCallback(
    (id: string) => {
      const topicName = trackedTopics.find(t => t.id === id)?.name;

      setTrackedTopics(prev =>
        prev.flatMap(t => {
          if (t.id !== id) return [t];
          if (t.status === 'added') return [];
          return [{ ...t, status: 'deleted' as const }];
        }),
      );

      setTrackedSubs(prev =>
        prev.flatMap(s => {
          if (s.topic !== topicName) return [s];
          if (s.status === 'added') return [];
          return [{ ...s, status: 'deleted' as const }];
        }),
      );

      if (selectedTopic === topicName) {
        setSelectedTopic(null);
      }
    },
    [selectedTopic, trackedTopics],
  );

  const handleRestoreTopic = useCallback(
    (id: string) => {
      const topicName = trackedTopics.find(t => t.id === id)?.name;

      setTrackedTopics(prev =>
        prev.map(t => {
          if (t.id !== id) return t;
          return { ...t, status: 'original' as const };
        }),
      );

      // Also restore subscriptions for this topic
      setTrackedSubs(prev =>
        prev.map(s => {
          if (s.topic !== topicName) return s;
          return { ...s, status: 'original' as const };
        }),
      );
    },
    [trackedTopics],
  );

  // Subscription CRUD handlers
  const handleAddSubscription = useCallback(
    (subscription: ServiceBusSubscription) => {
      setTrackedSubs(prev => [
        ...prev,
        {
          ...subscription,
          id: `sub:${subscription.topic}/${subscription.name}`,
          status: 'added',
        },
      ]);
    },
    [],
  );

  const handleEditSubscription = useCallback(
    (id: string, subscription: ServiceBusSubscription) => {
      setTrackedSubs(prev =>
        prev.map(s => {
          if (s.id !== id) return s;
          return {
            ...subscription,
            id: s.id,
            status: s.status === 'added' ? 'added' : 'modified',
          };
        }),
      );
    },
    [],
  );

  const handleDeleteSubscription = useCallback((id: string) => {
    setTrackedSubs(prev =>
      prev.flatMap(s => {
        if (s.id !== id) return [s];
        if (s.status === 'added') return [];
        return [{ ...s, status: 'deleted' as const }];
      }),
    );
  }, []);

  const handleRestoreSubscription = useCallback((id: string) => {
    setTrackedSubs(prev =>
      prev.map(s => {
        if (s.id !== id) return s;
        return { ...s, status: 'original' as const };
      }),
    );
  }, []);

  // Count changes for summary
  const addedTopics = trackedTopics.filter(t => t.status === 'added').length;
  const modifiedTopics = trackedTopics.filter(
    t => t.status === 'modified',
  ).length;
  const deletedTopics = trackedTopics.filter(
    t => t.status === 'deleted',
  ).length;
  const addedSubs = trackedSubs.filter(s => s.status === 'added').length;
  const modifiedSubs = trackedSubs.filter(s => s.status === 'modified').length;
  const deletedSubs = trackedSubs.filter(s => s.status === 'deleted').length;
  const hasChanges =
    addedTopics +
      modifiedTopics +
      deletedTopics +
      addedSubs +
      modifiedSubs +
      deletedSubs >
    0;

  const hasError = rawErrors?.length > 0 || !!error;

  if (!environment) {
    return (
      <Flex direction="column" className={styles.root}>
        <Text variant="title-small">
          {schema.title || 'Tópicos e Subscriptions'}
        </Text>
        <Text variant="body-small" color="secondary">
          Selecione um ambiente acima para carregar a configuração do Service
          Bus.
        </Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" className={styles.root}>
      <div className={styles.header}>
        <Text variant="title-small">
          {schema.title || 'Tópicos e Subscriptions'} —{' '}
          {environment.toUpperCase()}
        </Text>
        <TooltipTrigger>
          <ButtonIcon
            aria-label="Recarregar do GitLab"
            variant="secondary"
            icon={<RiRefreshLine size={16} />}
            onPress={() => {
              setInitialized(false);
              reload();
            }}
          />
          <Tooltip>Recarregar do GitLab</Tooltip>
        </TooltipTrigger>
      </div>

      {loading && (
        <div className={styles.loadingBox}>
          <Skeleton width="16rem" height="1.5rem" />
          <Text variant="body-small">
            Carregando configuração do Service Bus...
          </Text>
        </div>
      )}

      {error && (
        <Alert
          status="danger"
          icon
          title="Erro ao carregar configuração"
          description={error.message}
        />
      )}

      {!loading && !error && initialized && (
        <>
          <Grid.Root columns={{ sm: '12' }} gap="4">
            <Grid.Item colSpan={{ sm: '12', md: '4' }}>
              <div className={styles.panel}>
                <TopicList
                  topics={trackedTopics}
                  onAdd={handleAddTopic}
                  onEdit={handleEditTopic}
                  onDelete={handleDeleteTopic}
                  onRestore={handleRestoreTopic}
                  selectedTopic={selectedTopic}
                  onSelectTopic={setSelectedTopic}
                />
              </div>
            </Grid.Item>
            <Grid.Item colSpan={{ sm: '12', md: '8' }}>
              <div className={styles.panel}>
                <SubscriptionList
                  subscriptions={trackedSubs}
                  topics={trackedTopics}
                  selectedTopic={selectedTopic}
                  onAdd={handleAddSubscription}
                  onEdit={handleEditSubscription}
                  onDelete={handleDeleteSubscription}
                  onRestore={handleRestoreSubscription}
                />
              </div>
            </Grid.Item>
          </Grid.Root>

          {hasChanges && (
            <div className={styles.summary}>
              <Text variant="title-x-small">Resumo das alterações:</Text>
              <Text variant="body-small">
                Tópicos:{' '}
                {addedTopics > 0 && `+${addedTopics} novo(s) `}
                {modifiedTopics > 0 && `${modifiedTopics} editado(s) `}
                {deletedTopics > 0 && `-${deletedTopics} removido(s) `}
              </Text>
              <Text variant="body-small">
                Subscriptions:{' '}
                {addedSubs > 0 && `+${addedSubs} novo(s) `}
                {modifiedSubs > 0 && `${modifiedSubs} editado(s) `}
                {deletedSubs > 0 && `-${deletedSubs} removido(s) `}
              </Text>
            </div>
          )}
        </>
      )}

      {hasError && !error && (
        <Alert
          status="danger"
          icon
          title="Campo obrigatório"
          description={rawErrors?.join(', ') || 'Campo obrigatório'}
        />
      )}
    </Flex>
  );
};
